<script lang="ts">
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Contact } from '#library/components.schema.build.ts'
	import Image from '#components/image.svelte'
	import Field from '#components/field.svelte'
	import Checkbox from '#components/checkbox.svelte'
	import Button from '#components/button.svelte'
	import { enhance } from '$app/forms'
	import { slide } from 'svelte/transition'

	interface Props {
		blok: Blok<Contact>
	}

	let { blok }: Props = $props()
	let form = $state<HTMLFormElement | null>(null)

	let form_state: 'idle' | 'submitting' | 'success' | 'error' = $state('idle')

	type SubmitFunction = Parameters<typeof enhance>[1]

	const submit: SubmitFunction = () => {
		form_state = 'submitting'

		return async ({ result }) => {
			switch (result.type) {
				case 'success':
					form_state = 'success'
					break

				case 'error':
				case 'failure':
					form_state = 'error'
					break
			}
		}
	}
</script>

<section class="contact {blok.theme}" {...attrs(blok)}>
	<div class="contain" class:alt={blok.alt}>
		{#if blok.alt}
			<div class="title alt">{blok.title}</div>
		{/if}
		<div>
			{#if !blok.alt}
				<div class="title">{blok.title}</div>
			{/if}
			<div class="subtitle">{blok.subtitle}</div>
			{#if blok.image?.filename}
				<Image src={blok.image.filename} alt={blok.title} focus={blok.image.focus} />
			{/if}
		</div>
		<form
			action="/?/contact"
			bind:this={form}
			class="form"
			class:alt={blok.alt}
			method="post"
			use:enhance={submit}
		>
			{#if form_state === 'error'}
				<span class="error" transition:slide>Something went wrong, please try again.</span>
			{/if}
			<Field name="name" label="What's your name?" placeholder="Enter your name" required />
			<Field
				name="email"
				label="Your email (so we can get back to you)"
				placeholder="Enter your email"
				required
			/>
			<Field
				type="select"
				name="subject"
				label="What’s your message about?"
				placeholder="Select your subject"
				required
				options={['General', 'Booking', 'Other']}
			/>
			<Field
				type="textarea"
				name="message"
				label="Tell us more—we’re here to help."
				placeholder="e.g. I'd like more information about an event."
				required
				rows={5}
			/>
			<Checkbox
				name="terms"
				options={[
					{
						id: 'terms',
						value: 'Agreed',
						label: 'I have read and understood the terms & conditions & privacy policy',
						required: true
					}
				]}
			/>
			<Button type="submit" fill loading={form_state === 'submitting'}>Send message</Button>
			{#if form_state === 'success'}
				<div class="success">
					<div class="suptitle">Message sent</div>
					<div class="subtitle">A member of the team will be in touch as soon as possible</div>
					<Button
						type="button"
						fill
						onclick={() => {
							form_state = 'idle'
							form?.reset()
						}}
					>
						Send another message
					</Button>
				</div>
			{/if}
		</form>
	</div>
</section>

<style lang="css">
	.contact {
		background-color: var(--c-background);
		padding-block: var(--padding-top, var(--padding-default)) var(--padding-bottom, var(--padding-default));
	}

	.contain {
		display: grid;
		gap: 1.5rem;

		@media (--from-tablet) {
			gap: 2.5rem;
			grid-template-columns: repeat(2, minmax(0, 1fr));

			&.alt {
				grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
			}
		}
	}

	.form {
		background-color: var(--c-gimson);
		display: grid;
		gap: 1.5rem;
		padding: 1rem;

		@media (--from-tablet) {
			padding: 2rem;

			&.alt {
				order: -1;
			}
		}
	}

	.title {
		font: 600 var(--f-h3);
		grid-column: 1 / -1;
		letter-spacing: var(--ls-h3);
		margin-bottom: 1.25rem;

		&.alt {
			font: 600 var(--f-h2);
			letter-spacing: var(--ls-h2);
			grid-column: 1 / -1;
			order: -1;
		}
	}

	.subtitle {
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
		margin-bottom: 3rem;
	}

	.success {
		align-content: start;
		background-color: var(--c-gimson);
		display: grid;
		gap: 1.5rem;
		inset: 0;
		padding: 2rem;
		position: absolute;

		.subtitle {
			margin-block-end: 1.375rem;
		}
	}

	.suptitle {
		color: var(--c-foreground);
		font: 600 var(--f-h4);
		letter-spacing: var(--ls-h4);
	}

	.error {
		color: var(--c-rose);
		font: 400 var(--f-body1);
		letter-spacing: var(--ls-body1);
	}
</style>
